export type CardMessageInput = {
  text: string;
  vetName?: string | null;
};

export type MessageSlot = {
  page: 2 | 3;
  position: "full" | "top" | "bottom";
  maxLines: number;
};

export type WrappedLineEstimateOptions = {
  /** Must match Cardly's template font as closely as possible. Size is less important than family/style. */
  font?: string;
  /** Longest known Cardly line that exactly fits horizontally. */
  calibrationLine?: string;
  /** Reduce usable width to avoid browser/Cardly rendering differences. */
  safetyFactor?: number;
  /** Optional hook for tests/non-browser callers. */
  measureText?: (text: string) => number;
};

export type MessageValidationResult = {
  fits: boolean;
  usedLines: number;
  maxLines: number;
  remainingLines: number;
};

export type MessagesValidationResult = {
  fits: boolean;
  messageCount: number;
  results: MessageValidationResult[];
};

export const CARDLY_WRITABLE_PAGES = [2, 3] as const;
export const CARDLY_MAX_MESSAGES = 4;
export const CARDLY_FULL_PAGE_LINES = 20;
export const CARDLY_HALF_PAGE_LINES = 10;
export const CARDLY_DEFAULT_PRINT_FONT = "25px Arial";
export const CARDLY_CALIBRATION_LINE = "Lorem ipsum dolor sit amet, consectetur";
export const CARDLY_DEFAULT_SAFETY_FACTOR = 0.95;
export const CARDLY_SENDER_PREFIX = "From ";

let canvasContext: CanvasRenderingContext2D | null = null;

function browserMeasureText(font: string): (text: string) => number {
  if (typeof document === "undefined") {
    // Non-browser fallback. Since the calibration line and candidate lines both use
    // this same metric, this still gives a useful conservative approximation.
    return (text) => Array.from(text).length;
  }

  canvasContext ??= document.createElement("canvas").getContext("2d");

  if (!canvasContext) {
    return (text) => Array.from(text).length;
  }

  canvasContext.font = font;
  return (text) => canvasContext?.measureText(text).width ?? Array.from(text).length;
}

function getMeasureText(options: WrappedLineEstimateOptions): (text: string) => number {
  return options.measureText ?? browserMeasureText(options.font ?? CARDLY_DEFAULT_PRINT_FONT);
}

function splitLongToken(
  token: string,
  maxWidth: number,
  measureText: (text: string) => number,
): string[] {
  const characters = Array.from(token);
  const lines: string[] = [];
  let currentLine = "";

  characters.forEach((character) => {
    const candidate = currentLine + character;

    if (currentLine === "" || measureText(candidate) <= maxWidth) {
      currentLine = candidate;
    } else {
      lines.push(currentLine);
      currentLine = character;
    }
  });

  if (currentLine !== "") lines.push(currentLine);

  return lines;
}

function estimateParagraphLines(
  paragraph: string,
  maxWidth: number,
  measureText: (text: string) => number,
): number {
  const tokens = paragraph.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 1;

  let lineCount = 1;
  let currentLine = "";

  tokens.forEach((token) => {
    const candidate = currentLine ? `${currentLine} ${token}` : token;

    if (measureText(candidate) <= maxWidth) {
      currentLine = candidate;
      return;
    }

    if (currentLine !== "") {
      lineCount += 1;
    }

    if (measureText(token) <= maxWidth) {
      currentLine = token;
      return;
    }

    const splitLines = splitLongToken(token, maxWidth, measureText);
    lineCount += Math.max(splitLines.length - 1, 0);
    currentLine = splitLines[splitLines.length - 1] ?? "";
  });

  return lineCount;
}

/**
 * Estimate how many Cardly print lines the text will consume after wrapping.
 * Manual newlines are counted as forced line breaks; blank manual lines consume one line.
 */
export function estimateWrappedLines(
  text: string,
  options: WrappedLineEstimateOptions = {},
): number {
  if (text.trim() === "") return 0;

  const measureText = getMeasureText(options);
  const calibrationLine = options.calibrationLine ?? CARDLY_CALIBRATION_LINE;
  const safetyFactor = options.safetyFactor ?? CARDLY_DEFAULT_SAFETY_FACTOR;
  const maxWidth = measureText(calibrationLine) * safetyFactor;

  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .reduce(
      (lineCount, paragraph) =>
        lineCount + estimateParagraphLines(paragraph, maxWidth, measureText),
      0,
    );
}

export function estimateMessageLines(
  message: CardMessageInput,
  options: WrappedLineEstimateOptions = {},
): number {
  const senderText = message.vetName?.trim()
    ? `${CARDLY_SENDER_PREFIX}${message.vetName.trim()}`
    : "";

  return estimateWrappedLines(senderText, options) + estimateWrappedLines(message.text, options);
}

export function maxLinesPerMessage(messageCount: number): number {
  if (messageCount < 1 || messageCount > CARDLY_MAX_MESSAGES) {
    throw new Error(`Cardly supports between 1 and ${CARDLY_MAX_MESSAGES} messages.`);
  }

  return messageCount <= 2 ? CARDLY_FULL_PAGE_LINES : CARDLY_HALF_PAGE_LINES;
}

export function getMessageSlots(messageCount: number): MessageSlot[] {
  if (messageCount < 1 || messageCount > CARDLY_MAX_MESSAGES) {
    throw new Error(`Cardly supports between 1 and ${CARDLY_MAX_MESSAGES} messages.`);
  }

  if (messageCount === 1) return [{ page: 3, position: "full", maxLines: CARDLY_FULL_PAGE_LINES }];

  if (messageCount === 2) {
    return [
      { page: 2, position: "full", maxLines: CARDLY_FULL_PAGE_LINES },
      { page: 3, position: "full", maxLines: CARDLY_FULL_PAGE_LINES },
    ];
  }

  if (messageCount === 3) {
    return [
      { page: 2, position: "top", maxLines: CARDLY_HALF_PAGE_LINES },
      { page: 3, position: "top", maxLines: CARDLY_HALF_PAGE_LINES },
      { page: 3, position: "bottom", maxLines: CARDLY_HALF_PAGE_LINES },
    ];
  }

  return [
    { page: 2, position: "top", maxLines: CARDLY_HALF_PAGE_LINES },
    { page: 2, position: "bottom", maxLines: CARDLY_HALF_PAGE_LINES },
    { page: 3, position: "top", maxLines: CARDLY_HALF_PAGE_LINES },
    { page: 3, position: "bottom", maxLines: CARDLY_HALF_PAGE_LINES },
  ];
}

export function validateMessage(
  message: CardMessageInput,
  messageCount: number,
  options: WrappedLineEstimateOptions = {},
): MessageValidationResult {
  const usedLines = estimateMessageLines(message, options);
  const maxLines = maxLinesPerMessage(messageCount);

  return {
    fits: usedLines <= maxLines,
    usedLines,
    maxLines,
    remainingLines: maxLines - usedLines,
  };
}

/**
 * Validate the set of messages that would actually be sent to Cardly.
 * Empty message bodies are ignored, matching CardCreator's send behavior.
 */
export function validateMessages(
  messages: CardMessageInput[],
  options: WrappedLineEstimateOptions = {},
): MessagesValidationResult {
  const filledMessages = messages.filter((message) => message.text.trim() !== "");

  if (filledMessages.length === 0) {
    return { fits: false, messageCount: 0, results: [] };
  }

  if (filledMessages.length > CARDLY_MAX_MESSAGES) {
    return {
      fits: false,
      messageCount: filledMessages.length,
      results: filledMessages.map((message) => ({
        ...validateMessage(message, CARDLY_MAX_MESSAGES, options),
        fits: false,
      })),
    };
  }

  const results = filledMessages.map((message) =>
    validateMessage(message, filledMessages.length, options),
  );

  return {
    fits: results.every((result) => result.fits),
    messageCount: filledMessages.length,
    results,
  };
}
